<template>
  <v-sheet class="pa-4 w-100 fill-height border rounded overflow-auto" elevation="1">
    <v-row class="justify-start pa-4 text-uppercase text-body-1 font-weight-regular">
      {{ isValidating ? $t('messages.validating') : $t('messages.validations') }}
    </v-row>
    <v-row class="w-100 justify-start" v-if="isValidating">
      <v-col v-for="i in 4" :key="i" width="" cols="12" class="pa-0">
        <v-skeleton-loader type="chip" class="pa-1"></v-skeleton-loader>
      </v-col>
    </v-row>

    <v-row v-else class="px-4">
      <v-chip-group column>
        <v-chip
          v-for="validation in platformValidation"
          :key="validation.name"
          class="text-body-1 font-weight-medium"
          variant="outlined"
          disabled
          v-show="!hideMinorOverlapvalidation || validation.name !== 'Minor Element Overlaps'"
          style="pointer-events: none; opacity: 1"
        >
          <template v-slot:prepend>
            <v-icon class="mr-1" :color="getChipColor(validation.status)">
              {{ getChipIcon(validation.status) }}
            </v-icon>
          </template>
          {{ validation.name }}
        </v-chip>
      </v-chip-group>
      <v-list align="start" outlined class="pb-0">
        <v-list-item
          v-for="validation in platformValidation.filter(
            (item) => item.status === 'fail' || item.status === 'warn',
          )"
          :key="validation.name"
          class="d-flex align-start pa-2"
        >
          <template v-slot:prepend>
            <v-icon
              :icon="getChipIcon(validation.status)"
              :class="`text-${getChipColor(validation.status)}`"
            ></v-icon>
          </template>
          <span class="text-body-1">{{ validation.details }}</span>
        </v-list-item>
      </v-list>
    </v-row>
  </v-sheet>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { Validation } from '@/types/validation'

const props = defineProps<{ platformValidation: Validation[]; isValidating: boolean }>()

const getChipColor = (status: Validation['status']): string => {
  switch (status) {
    case 'pass':
      return 'green lighten-4'
    case 'fail':
      return 'red lighten-4'
    case 'partial':
      return 'yellow lighten-4'
    case 'warn':
      return 'orange lighten-4'
    default:
      return 'grey lighten-2'
  }
}

const getChipIcon = (status: Validation['status']): string => {
  switch (status) {
    case 'pass':
      return 'mdi-check'
    case 'fail':
      return 'mdi-close'
    case 'partial':
      return 'mdi-minus'
    case 'warn':
      return 'mdi-alert'
    default:
      return 'mdi-help'
  }
}

const hideMinorOverlapvalidation = computed(() => {
  const failOverlapValidation = props.platformValidation.find(
    (validation: Validation) => validation.name === 'Element Overlaps',
  )
  if (!failOverlapValidation) return false
  const minorOverlapValidation = props.platformValidation.find(
    (validation: Validation) => validation.name === 'Minor Element Overlaps',
  )
  if (minorOverlapValidation) return true
  return false
})
</script>

<style scoped>
:deep(.v-list-item__prepend > .v-icon) {
  opacity: 1 !important;
}
:deep(.v-skeleton-loader__chip) {
  margin: 0 !important;
  max-width: 200px;
  height: 40px;
}
</style>
